import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import {
	list,
	LiveInformationEventResponseModel,
	listMore,
	EventInterests,
	LiveInformationEventResponseModelData,
	LiveInformationEventRequestModel
} from "client/Api/Workspace/LiveInformation/Hub/eventsLiveInformation";
import {
	HubItemCategoryIdentifierModel
} from "client/Api/Hub/common";
import { Guid } from "client_legacy/ndaw/guid";
import {
	EventPagingInformationModel,
	EventPreviewModel
} from "client/Api/Hub/Events/eventViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubTileCategoryIds } from "client/Features/Workspace/Tiles/Hub/common";
import { ensureNotNil } from "client/Misc/utils";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { never } from "client_legacy/ndaw/errors";
import { HubItemTileResourceId, HubItemTileResourceKind } from "client/Api/Workspace/workspace";
import { listCategories } from "client/Api/Workspace/LiveInformation/Hub/hubItemCategoriesLiveInformation";

export interface InterestedEventParty extends InterestedParty<Guid, HubItemTileResourceId> {
	updateItems?(page: LazyLoadPageResult<EventPreviewModel>): void;
}

type EventLiveInformationProviderOptions = {
	intervalInSeconds: number;
};

export class EventLiveInformationProvider extends LiveInformationProvider<
	EventLiveInformationProviderOptions,
	Guid,
	HubItemTileResourceId,
	EventInterests,
	InterestedEventParty,
	LiveInformationEventResponseModelData
>
{
	private _listRetrievalPromise: Promise<HubItemCategoryIdentifierModel[]> | undefined;

	public get itemKind(): HubItemKind {
		return HubItemKind.Event;
	}

	constructor(options: EventLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[HubItemTileResourceId, EventInterests]>
	): Promise<ReadonlyArray<[HubItemTileResourceId, LiveInformationEventResponseModelData]>> {
		const requestModels: LiveInformationEventRequestModel[] = entries.map(x => {
			const [resource, interests] = x;
			return {
				resource,
				interests
			};
		});

		const responses: readonly LiveInformationEventResponseModel[] = await list(requestModels);

		return responses.map(x => [x.resource, x.data]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<EventInterests, LiveInformationEventResponseModelData>,
		response: [EventInterests, LiveInformationEventResponseModelData]
	): void {
		const [retrievedInterests, data] = response;

		if (retrievedInterests.items) {
			cacheItem.retrievedInterests.items = true;
			cacheItem.data.items = data.items;
		}
	}

	protected informParty(
		party: InterestedEventParty,
		cacheItem: [EventInterests, LiveInformationEventResponseModelData]
	): void {
		const [retrievedInterests, data] = cacheItem;
		const interestedIn: EventInterests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.items && retrievedInterests.items) {
			party.updateItems?.(
				ensureNotNil(data.items)
			);
		}
	}

	protected override createResourceMap<TValue>(): Map<HubItemTileResourceId, TValue> {
		return new NormalizedMap<HubItemTileResourceId, TValue>(
			normalizeKey
		);
	}

	public reload(party: InterestedEventParty) {
		this.clearCacheForResource(party.resourceId);
		const interests: EventInterests = this.getInterestsForEnsuredParty(party);
		this.queuePartyForDataRetrieval(party, interests);
	}

	public listCategories(): Promise<HubItemCategoryIdentifierModel[]> {
		if (!this._listRetrievalPromise) {
			this._listRetrievalPromise = listCategories(HubItemKind.Event);
		}
		return this._listRetrievalPromise;
	}

	public loadMoreItems(
		categoryIds: HubTileCategoryIds,
		_interests: Required<EventInterests>,
		lastRetrievedItem: EventPreviewModel,
		signal?: AbortSignal
	): Promise<LazyLoadPageResult<EventPreviewModel>>
	{
		const paginationInfo: EventPagingInformationModel = {
			id: lastRetrievedItem.id,
			startDate: lastRetrievedItem.startDate
		};

		return listMore(
			{
				categoryIds,
				lastRetrievedItem: paginationInfo
			},
			signal
		);
	}
}

function normalizeKey(key: HubItemTileResourceId): string {
	switch (key.kind) {
		case HubItemTileResourceKind.CategoryIds:
			if (key.categoryIds === null) {
				return "categories|all";
			} else {
				return `categories|${key.categoryIds.join()}`;
			}
		case HubItemTileResourceKind.ItemIds:
			return `items|${key.itemIds.join()}`;
		default:
			never(key);
	}
}
